import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight,
  Phone,
  Heart,
  MessageCircleHeart,
  Users,
  MapPin,
  Clock,
  Quote,
  Star,
} from "lucide-react";
import { useSeo } from "../hooks/useSeo";
import { Reveal, LineReveal, fadeUp, stagger } from "../lib/motionx";
import { SectionLabel } from "../components/PageHeader";
import { GrowthJourney } from "../components/GrowthJourney";
import { InsurancesMarquee } from "../components/InsurancesMarquee";
import { useLang } from "../i18n";
import {
  site,
  services,
  trustPoints,
  marqueeWords,
  testimonials,
  serviceAreas,
} from "../data/site";

const HeroImage = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [0, 120]);

  return (
    <div ref={ref} className="relative">
      <motion.div
        initial={{ clipPath: "inset(100% 0 0 0)", opacity: 0 }}
        animate={{ clipPath: "inset(0% 0 0 0)", opacity: 1 }}
        transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.3 }}
        className="relative overflow-hidden rounded-t-[999px] rounded-b-[2rem] aspect-[4/5] bg-sage/20"
      >
        <motion.img
          style={{ y }}
          src="/brand/hero-clinic-v2.jpg"
          alt="A pediatric therapist in blue scrubs kneeling on a therapy mat, playing with colorful stacking rings and a happy smiling toddler in a bright clinic therapy room."
          className="h-[115%] w-full object-cover -mt-[7%]"
          loading="eager"
        />
      </motion.div>
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1, duration: 0.6, ease: "backOut" }}
        className="absolute -left-4 bottom-10 sm:left-0 rounded-2xl bg-white shadow-xl px-5 py-4 flex items-center gap-3"
      >
        <span className="grid place-items-center h-11 w-11 rounded-full bg-peach/30 text-teal">
          <Heart size={20} />
        </span>
        <div>
          <p className="font-heading font-semibold text-ink leading-tight">{t("Family-first care")}</p>
          <p className="text-xs text-ink/55">{t("Gentle, playful, patient")}</p>
        </div>
      </motion.div>
    </div>
  );
};

export default function Home() {
  const { t, lang } = useLang();
  useSeo({
    title: "Amenity Health Services | Pediatric Speech, ABA & Developmental Care",
    description:
      "Amenity Health Services provides compassionate speech, ABA, and developmental support for children and families in Houston, TX. Request information or call us today.",
  });

  const heroLines = lang === "es"
    ? ["Apoyo afectuoso", "que ayuda a los niños", "a encontrar su voz"]
    : site.heroHeadline;

  return (
    <>
      {/* HERO */}
      <section className="relative pt-28 sm:pt-32 pb-16 overflow-hidden">
        <div className="pointer-events-none absolute -top-24 -right-24 h-96 w-96 rounded-full bg-sage/20 blur-3xl" />
        <div className="pointer-events-none absolute top-40 -left-32 h-80 w-80 rounded-full bg-peach/20 blur-3xl" />
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center relative">
          <div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <SectionLabel className="mb-6">{t(site.tagline)}</SectionLabel>
            </motion.div>
            <LineReveal
              key={lang}
              lines={heroLines}
              className="font-heading font-semibold text-[13vw] leading-[0.95] sm:text-6xl lg:text-7xl tracking-tight text-ink"
            />
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.6 }}
              className="mt-7 max-w-xl text-lg text-ink/65 leading-relaxed"
            >
              {t(site.heroSub)}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.75, duration: 0.6 }}
              className="mt-9 flex flex-wrap gap-3"
            >
              <Link
                to="/contact"
                data-testid="hero-request-info-btn"
                className="group inline-flex items-center gap-2 rounded-full bg-teal px-7 py-4 font-medium text-white hover:bg-[#2E6088] transition-colors duration-200 shadow-sm"
              >
                {t("Request Information")}
                <ArrowRight size={18} className="transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
              <a
                href={site.phoneHref}
                data-testid="hero-call-btn"
                className="inline-flex items-center gap-2 rounded-full border border-teal/25 bg-white px-7 py-4 font-medium text-ink hover:border-teal/50 transition-colors duration-200"
              >
                <Phone size={18} className="text-teal" /> {t("Call Us")}
              </a>
              <Link
                to="/services"
                data-testid="hero-services-btn"
                className="inline-flex items-center gap-2 rounded-full px-7 py-4 font-medium text-ink/70 hover:text-teal transition-colors duration-200"
              >
                {t("Our Services")}
              </Link>
            </motion.div>
          </div>
          <div className="relative">
            <HeroImage />
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="py-6 border-y border-teal/10 bg-sage/30 overflow-hidden">
        <div className="flex gap-8 whitespace-nowrap animate-marquee w-max">
          {[...marqueeWords, ...marqueeWords].map((w, i) => (
            <span key={i} className="inline-flex items-center gap-8 font-heading text-2xl sm:text-3xl font-light text-ink/30">
              {t(w)}
              <Star size={16} className="text-peach fill-peach" />
            </span>
          ))}
        </div>
      </section>

      {/* WHO WE HELP */}
      <section className="py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-10 items-start">
          <Reveal className="lg:col-span-5">
            <SectionLabel className="mb-5">{t("Who we help")}</SectionLabel>
            <h2 className="font-heading font-semibold text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight text-ink">
              {t("Care built around your child, and your family.")}
            </h2>
          </Reveal>
          <Reveal className="lg:col-span-6 lg:col-start-7" delay={0.1}>
            <p className="text-lg text-ink/65 leading-relaxed">
              {t("We support children who are working on communication, behavior, and everyday developmental skills — and we support the families walking alongside them. From your very first question to ongoing sessions, our team keeps things warm, clear, and centered on what matters most to you.")}
            </p>
            <Link
              to="/services"
              data-testid="whohelp-learn-link"
              className="mt-6 inline-flex items-center gap-2 font-medium text-teal hover:gap-3 transition-all duration-200"
            >
              {t("Explore our services")} <ArrowRight size={18} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* GROWTH JOURNEY */}
      <GrowthJourney />

      {/* SERVICES OVERVIEW */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
            <div>
              <SectionLabel className="mb-4">{t("Our services")}</SectionLabel>
              <h2 className="font-heading font-semibold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-ink">
                {t("Ways we can support you")}
              </h2>
            </div>
            <Link
              to="/services"
              data-testid="services-overview-all-link"
              className="inline-flex items-center gap-2 font-medium text-teal hover:gap-3 transition-all"
            >
              {t("View all services")} <ArrowRight size={18} />
            </Link>
          </div>
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-60px" }}
            className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5"
          >
            {services.map((s) => (
              <motion.div key={s.slug} variants={fadeUp}>
                <Link
                  to={`/services#${s.slug}`}
                  data-testid={`service-card-${s.slug}`}
                  className="group block h-full rounded-3xl border border-black/5 bg-cream p-6 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(0,0,0,0.06)] transition-[transform,box-shadow] duration-300"
                >
                  <div className="overflow-hidden rounded-2xl mb-5 aspect-[4/3]">
                    <img
                      src={s.image}
                      alt={s.alt}
                      loading="lazy"
                      className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <h3 className="font-heading font-semibold text-lg text-ink leading-snug">{t(s.name)}</h3>
                  <p className="mt-2 text-sm text-ink/60 leading-relaxed line-clamp-4">{t(s.short)}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-teal">
                    {t("Learn more")} <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="py-20 sm:py-28 bg-sage/25">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <SectionLabel className="mb-5">{t("Why families choose us")}</SectionLabel>
            <h2 className="font-heading font-semibold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-ink max-w-2xl">
              {t("Trust, built through everyday care")}
            </h2>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {trustPoints.map((tp, i) => (
              <Reveal key={tp.title} delay={i * 0.08}>
                <div className="h-full rounded-3xl border border-black/5 bg-white p-7">
                  <span className="grid place-items-center h-12 w-12 rounded-2xl bg-teal/10 text-teal mb-5">
                    {[<Heart key="h" size={22} />, <MessageCircleHeart key="m" size={22} />, <Users key="u" size={22} />, <Star key="s" size={22} />][i]}
                  </span>
                  <h3 className="font-heading font-semibold text-lg text-ink">{t(tp.title)}</h3>
                  <p className="mt-2 text-sm text-ink/60 leading-relaxed">{t(tp.body)}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICE AREA */}
      <section className="py-16 sm:py-24 bg-ink text-cream overflow-hidden">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <SectionLabel className="mb-5 text-peach">{t("Service areas")}</SectionLabel>
            <h2 className="font-heading font-semibold text-3xl sm:text-4xl lg:text-5xl tracking-tight">
              {t("Proudly serving families across Houston")}
            </h2>
            <p className="mt-5 text-cream/70 leading-relaxed max-w-lg">
              {t("We work with families throughout the areas below. Not sure if you're in our service area? Reach out — we're always happy to help point you in the right direction.")}
            </p>
            <Link
              to="/service-areas"
              data-testid="home-service-areas-link"
              className="mt-7 inline-flex items-center gap-2 rounded-full bg-cream text-ink px-6 py-3 font-medium hover:bg-peach transition-colors"
            >
              <MapPin size={18} /> {t("See service areas")}
            </Link>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="flex flex-wrap gap-3">
              {serviceAreas.map((a) => (
                <span
                  key={a}
                  className="rounded-full border border-white/15 bg-white/5 px-5 py-2.5 text-sm text-cream/80"
                >
                  {t(a)}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <SectionLabel className="mb-5">{t("Kind words")}</SectionLabel>
            <div className="flex items-end justify-between gap-4 flex-wrap">
              <h2 className="font-heading font-semibold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-ink max-w-xl">
                {t("Stories from families")}
              </h2>
            </div>
          </Reveal>
          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {testimonials.map((tm, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <figure className="h-full rounded-3xl border border-black/5 bg-white p-7 flex flex-col">
                  <div className="flex items-center gap-0.5 mb-4">
                    {[...Array(5)].map((_, s) => (
                      <Star key={s} size={16} className="text-peach fill-peach" />
                    ))}
                  </div>
                  <Quote size={26} className="text-peach/70 mb-3" />
                  <blockquote className="text-ink/75 leading-relaxed flex-1 text-[0.95rem]">
                    {t(tm.quote)}
                  </blockquote>
                  <figcaption className="mt-6 pt-5 border-t border-black/5">
                    <p className="font-heading font-semibold text-ink">{t(tm.name)}</p>
                    <p className="text-sm text-ink/55">{t(tm.role)}</p>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-20 sm:pb-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <div className="relative overflow-hidden rounded-[2.5rem] bg-sage/25 px-8 py-14 sm:px-16 sm:py-20 text-center">
              <div className="pointer-events-none absolute -top-16 -right-16 h-64 w-64 rounded-full bg-peach/30 blur-3xl" />
              <h2 className="font-heading font-semibold text-3xl sm:text-5xl tracking-tight text-ink relative">
                {t("Ready to take the first step?")}
              </h2>
              <p className="mt-4 text-lg text-ink/65 max-w-xl mx-auto relative">
                {t("Tell us a little about your family, and we'll help you understand the next steps — at your pace.")}
              </p>
              <div className="mt-8 flex flex-wrap justify-center gap-3 relative">
                <Link
                  to="/contact"
                  data-testid="cta-request-info-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-teal px-8 py-4 font-medium text-white hover:bg-[#2E6088] transition-colors"
                >
                  {t("Request Information")} <ArrowRight size={18} />
                </Link>
                <a
                  href={site.phoneHref}
                  data-testid="cta-call-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 font-medium text-ink hover:bg-cream transition-colors"
                >
                  <Phone size={18} className="text-teal" /> {site.phone}
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CONTACT & HOURS */}
      <section className="pt-16 pb-24 bg-sage/25">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid md:grid-cols-2 gap-5">
          <Reveal>
            <div className="rounded-3xl border border-black/5 bg-white p-8 h-full">
              <h3 className="font-heading font-semibold text-xl text-ink mb-5">{t("Get in touch")}</h3>
              <ul className="space-y-4 text-ink/75">
                <li className="flex items-center gap-3">
                  <Phone size={18} className="text-teal shrink-0" />
                  <a href={site.phoneHref} className="hover:text-teal transition-colors">{site.phone}</a>
                </li>
                <li className="flex items-center gap-3">
                  <MessageCircleHeart size={18} className="text-teal shrink-0" />
                  <a href={`mailto:${site.email}`} className="hover:text-teal transition-colors">{site.email}</a>
                </li>
                <li className="flex items-start gap-3">
                  <MapPin size={18} className="text-teal shrink-0 mt-1" />
                  <Link to="/service-areas" className="hover:text-teal transition-colors">{t("View our locations")}</Link>
                </li>
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="rounded-3xl border border-black/5 bg-white p-8 h-full">
              <h3 className="font-heading font-semibold text-xl text-ink mb-5 flex items-center gap-2">
                <Clock size={20} className="text-teal" /> {t("Business hours")}
              </h3>
              <ul className="space-y-3">
                {site.hours.map((h) => (
                  <li key={h.day} className="flex items-center justify-between text-ink/75 border-b border-black/5 pb-3 last:border-0">
                    <span>{t(h.day)}</span>
                    <span className="font-medium text-ink">{t(h.time)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* INSURANCES */}
      <InsurancesMarquee />
    </>
  );
}
